import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { SiteLayout } from '@/components/layout/SiteLayout';
import { HomePage } from '@/features/marketing/HomePage';
import { ContactPage } from '@/features/contact/ContactPage';
import { AccountDeletionPage } from '@/features/legal/AccountDeletionPage';
import {
  BarbunnyPrivacyPage,
  BarbunnyTermsPage,
  CookiesPage,
  PrivacyPage,
  TermsPage,
} from '@/features/legal/pages';
import { NotFoundPage } from '@/features/misc/NotFoundPage';
import { AccesoDemoPage } from '@/features/demo/AccesoDemoPage';
import { AccesoInversionistaPage } from '@/features/inversionistas/AccesoInversionistaPage';
import { PanelInversionistaPage } from '@/features/inversionistas/PanelInversionistaPage';
import { PorteroInversionistasPage } from '@/features/inversionistas/PorteroInversionistasPage';
import { RecuperarContrasenaPage } from '@/features/inversionistas/RecuperarContrasenaPage';
import { AlAreaQueFunciona } from '@/features/inversionistas/identidad/AlAreaQueFunciona';
import { GuardiaDeSesion } from '@/features/inversionistas/identidad/GuardiaDeSesion';
import {
  RUTA_ACCESO,
  RUTA_AREA,
  RUTA_PANEL,
  RUTA_PORTERO,
  RUTA_RECUPERAR,
} from '@/features/inversionistas/rutas';
import { esPortadaDeInversionistas } from './portadaPorHostname';

/**
 * El sitio no tiene backend, y esto es lo que queda de aquello.
 *
 * Hasta el 2026-09-18 aquí colgaban cuatro pantallas de sesión, un panel de
 * administración, un panel de inversionistas, un portal de marcas de nueve
 * rutas, el alta de asistentes y las seis pantallas del estudio MICE. Todas
 * hablaban con un backend que se retiró; ninguna de ellas puede funcionar hoy,
 * así que ninguna sigue aquí. Lo que se conserva es lo que se sostiene solo:
 * marketing, contacto, los legales y la demostración, que es una simulación
 * que viaja entera en el bundle.
 *
 * **Estas rutas no se restauran solas.** Si algún día vuelve a haber backend,
 * vuelven del historial y con su capa de datos, no descomentando nada.
 */

/**
 * La demo de sensores se carga aparte.
 *
 * Trae un lienzo y una simulación que no abre todo el mundo; meterla en el
 * bundle principal se la haría descargar a todo el que entra al home. Es la
 * única ruta con carga diferida del proyecto.
 *
 * La portada de la demo (`AccesoDemoPage`) **no** va diferida: es un formulario
 * de tres campos, y hacer esperar a quien acaba de pulsar el botón para
 * enseñarle un login de mentira sería cambiar peso por parpadeo.
 */
const DemoSensoresPage = lazy(() =>
  import('@/features/demo/DemoSensoresPage').then((m) => ({ default: m.DemoSensoresPage })),
);

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        {/*
          La portada depende del nombre por el que se entra.

          `window.location.hostname` se lee **aquí y en ningún otro sitio**: un
          componente del área que decidiera por hostname estaría tomando dos
          veces la misma decisión, y la segunda acabaría divergiendo.

          Es un `Navigate` y no un render en el sitio: así la URL queda visible y
          honesta, no hay dos rutas pintando la misma pantalla, y el guardia de
          sesión se encarga desde ahí. No hay bucle posible porque el destino
          nunca es `/`.
        */}
        <Route
          path="/"
          element={
            esPortadaDeInversionistas(window.location.hostname) ? (
              <Navigate to={RUTA_AREA} replace />
            ) : (
              <HomePage />
            )
          }
        />
        <Route path="/contacto" element={<ContactPage />} />

        {/* Legales de ILUMETRIA (empresa) */}
        <Route path="/privacidad" element={<PrivacyPage />} />
        <Route path="/terminos" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiesPage />} />

        {/* Legales de Bar Bunny (app de fidelización) */}
        <Route path="/barbunny/privacidad" element={<BarbunnyPrivacyPage />} />
        <Route path="/barbunny/terminos" element={<BarbunnyTermsPage />} />
        <Route path="/barbunny/eliminar-cuenta" element={<AccountDeletionPage />} />

        {/*
          Rutas heredadas de cuponera.ilumetria.com. Ese sitio publicó
          /privacy y /terms, y puede haber enlaces vivos (fichas de tienda,
          correos) apuntando ahí. Redirigen en lugar de dar 404.
        */}
        <Route path="/privacy" element={<Navigate to="/barbunny/privacidad" replace />} />
        <Route path="/terms" element={<Navigate to="/barbunny/terminos" replace />} />

        {/*
          La demostración abierta: el panel que vería una marca, sin sesión y
          con un login de mentira delante.

          Sin puerta, y es una decisión de producto: sirve para enseñar el
          producto a quien todavía no tiene cuenta —en una sala, por
          videollamada, en un enlace de un correo—. Se puede porque aquí no hay
          nada que proteger: la simulación entera viaja en el bundle y no
          consulta ningún backend. El día que esta pantalla toque datos reales,
          esta ruta desaparece.
        */}
        <Route path="/demo" element={<AccesoDemoPage />} />
        <Route
          path="/demo/panel"
          element={
            <Suspense fallback={null}>
              <DemoSensoresPage />
            </Suspense>
          }
        />

        {/*
          El área de inversionistas.

          `RUTA_ACCESO` va **fuera** del guardia: es la pantalla a la que el
          guardia manda, así que ponerla dentro sería un bucle.

          La demo del área reusa `DemoSensoresPage`, el mismo componente y la
          misma carga diferida que `/demo`. Reusar el componente **no es fusionar
          las dos puertas**: `/demo` sigue siendo la demostración pública de
          marca, se entra desde el sitio de marketing, tiene su propio acceso
          simulado y no se toca. Ésta está detrás de la sesión.
        */}
        {/*
          `AlAreaQueFunciona` envuelve a las TRES puertas del area --acceso,
          recuperar y el guardia-- porque las tres se pintan igual en
          `www.ilumetria.com` y ninguna puede hablar con la API desde ahi.
        */}
        <Route
          path={RUTA_ACCESO}
          element={
            <AlAreaQueFunciona>
              <AccesoInversionistaPage />
            </AlAreaQueFunciona>
          }
        />
        {/* Fuera del guardia, como la puerta: quien la usa no puede entrar. */}
        <Route
          path={RUTA_RECUPERAR}
          element={
            <AlAreaQueFunciona>
              <RecuperarContrasenaPage />
            </AlAreaQueFunciona>
          }
        />
        <Route
          path={RUTA_AREA}
          element={
            <AlAreaQueFunciona>
              <GuardiaDeSesion />
            </AlAreaQueFunciona>
          }
        >
          <Route index element={<Navigate to={RUTA_PANEL} replace />} />
          <Route path="panel" element={<PanelInversionistaPage />} />
          {/*
            La administracion va DENTRO del guardia, como el panel, y sin
            ningun guardia de rol extra delante. No es un descuido: un guardia
            de rol en el router decidiria con lo que dice el `localStorage` de
            quien mira, que es de quien mira. Quien decide es el 403 de
            `capital`, y la pantalla ya sabe pintarlo.
          */}
          <Route
            path={RUTA_PORTERO.slice(RUTA_AREA.length + 1)}
            element={<PorteroInversionistasPage />}
          />
          <Route
            path="demo"
            element={
              <Suspense fallback={null}>
                <DemoSensoresPage />
              </Suspense>
            }
          />
        </Route>

        {/*
          Lo que se retiró el 2026-09-18 con el backend. Mandan al home en vez
          de dar 404 porque son URLs que circulan: `/login` y `/marcas` están en
          marcadores y en correos viejos, y `/participa` y `/estudio` se
          repartieron por WhatsApp a los participantes del estudio. Un 404 a
          quien vuelve a un enlace que le dimos nosotros parece un sitio roto;
          el home al menos explica qué es ILUMETRIA hoy.
        */}
        {[
          '/login',
          '/crear-cuenta',
          '/confirmar-cuenta',
          '/recuperar-contrasena',
          '/registro',
          '/mi-espacio',
          '/admin',
          '/participa',
          '/estudio',
          '/estudio/*',
          '/marcas',
          '/marcas/*',
        ].map((ruta) => (
          <Route key={ruta} path={ruta} element={<Navigate to="/" replace />} />
        ))}

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
